import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { Permission, Role } from '@pharmacia/core'
import { ApiError, get, post, setUnauthenticatedHandler } from './api.ts'
import { getSupabase, isOAuthReturn, supabaseReady } from './supabase.ts'

export type Me = {
  staff: { id: string; email: string; fullName: string; role: Role }
  permissions: Permission[]
}

type Status = 'booting' | 'signed-out' | 'signed-in'

type AuthValue = {
  status: Status
  me: Me | null
  error: string | null
  busy: boolean
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  can: (p: Permission) => boolean
  clearError: () => void
}

const Ctx = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('booting')
  const [me, setMe] = useState<Me | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Any 401 from anywhere in the app drops straight back to sign-in,
  // rather than leaving a half-usable screen full of failed panels.
  useEffect(() => {
    setUnauthenticatedHandler(() => { setMe(null); setStatus('signed-out') })
  }, [])

  useEffect(() => {
    let cancelled = false

    async function boot() {
      // 1. Coming back from Google? Exchange the token for our cookie.
      //    Only this branch pulls in the Supabase client.
      if (supabaseReady && isOAuthReturn()) await exchange(cancelled)

      // 2. Either way, ask the API who we are. The cookie is the truth.
      try {
        const data = await get<Me>('/auth/me')
        if (cancelled) return
        setMe(data)
        setStatus('signed-in')
      } catch {
        if (cancelled) return
        setStatus('signed-out')
      }
    }

    async function exchange(isCancelled: boolean) {
      const supabase = await getSupabase()
      try {
        const { data } = await supabase.auth.getSession()
        const token = data.session?.access_token
        if (!token) return
        await post('/auth/session', { accessToken: token })
      } catch (e) {
        if (!isCancelled) {
          setError(e instanceof ApiError
            ? e.message
            : 'Sign-in did not complete. Please try again.')
        }
      } finally {
        // Discard the Supabase token either way: from here on the only
        // credential is the HttpOnly cookie, which JavaScript cannot read.
        try { await supabase.auth.signOut({ scope: 'local' }) } catch { /* ignore */ }
        // Strip the token out of the address bar so it is not left in
        // history, bookmarks or a shoulder-surfer's view.
        window.history.replaceState({}, '', window.location.pathname)
      }
    }

    void boot()
    return () => { cancelled = true }
  }, [])

  const signIn = useCallback(async () => {
    setError(null)
    if (!supabaseReady) {
      setError('Sign-in is not configured. SUPABASE_URL and SUPABASE_PUBLISHABLE_KEY are missing from .env.')
      return
    }
    setBusy(true)
    const supabase = await getSupabase()
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin + '/' },
    })
    if (err) {
      setBusy(false)
      setError(err.message)
    }
    // On success the browser navigates away; no need to clear busy.
  }, [])

  const signOut = useCallback(async () => {
    setBusy(true)
    try { await post('/auth/logout') } catch { /* sign out locally regardless */ }
    setMe(null)
    setStatus('signed-out')
    setBusy(false)
  }, [])

  const value = useMemo<AuthValue>(() => ({
    status, me, error, busy, signIn, signOut,
    // Mirrors the server's matrix so menus match what the API will allow.
    // This hides things; it does not protect them -- the API is the guard.
    can: p => Boolean(me?.permissions.includes(p)),
    clearError: () => setError(null),
  }), [status, me, error, busy, signIn, signOut])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthValue {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
